import React, { ReactNode, useEffect, useMemo, useState } from 'react';

import * as amplitude from '@amplitude/analytics-browser';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Stack, Tooltip } from '@mui/material';

import { useTransactionProvider } from '~components/providers/TransactionProvider';
import { TxStatus } from '~components/providers/types';
import { LoadingText } from '~components/shared/loading/LoadingText';
import { statusRed } from '~components/theme/styles/colors';
import { IValidationResponse } from '~utils/validation/_types';

import { ButtonWithIcon, ButtonWithIconProps } from '../buttonWithIcon';

type ISendButtonProps = {
  iconSize?: number;
  validation?: IValidationResponse;
  confirmContent?: string | React.ReactElement;
  sendingContent?: string | React.ReactElement;
  successContent?: string | React.ReactElement;
  failedContent?: string | React.ReactElement;
  txKey: string;
  disabled?: boolean;
  onThen?(): void;
  onCatch?(): void;
};

type TransactionButtonProps = ButtonWithIconProps & ISendButtonProps;
export const AFTER_TX_TIMEOUT_MS = 3000;

export const TransactionButton = ({
  children,
  onClick,
  disabled,
  validation,
  confirmContent,
  sendingContent,
  successContent,
  txKey,
  onCatch,
  onThen,
  failedContent,
  iconSize = 16,
  variant = 'primaryGradient',
  ...props
}: TransactionButtonProps) => {
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [content, setButtonContent] = useState<ReactNode>('');
  const { createTxInstance, getTx } = useTransactionProvider();
  const currentTx = getTx(txKey);

  const state = useMemo(() => {
    if (!currentTx) return TxStatus.idle;

    return currentTx.status;
  }, [currentTx]);

  useEffect(() => {
    setButtonDisabled(!!validation?.error || disabled || !!currentTx?.isTransacting);

    switch (state) {
      case 'confirming':
        setButtonContent(
          <Stack width={'100%'} justifyContent={'space-between'} direction={'row'} alignItems={'center'}>
            {confirmContent ?? 'CONFIRM'} &nbsp;
            <LoadingText repeatSymbol={3} symbol={'.'} />{' '}
          </Stack>,
        );
        break;
      case 'sending':
        setButtonContent(
          <Stack width={'100%'} justifyContent={'space-between'} direction={'row'} alignItems={'center'}>
            {sendingContent ?? 'SENDING '}
            &nbsp;
            <LoadingText repeatSymbol={3} symbol={'.'} />
          </Stack>,
        );
        break;
      case 'success':
        setButtonContent(
          <Stack width={'100%'} justifyContent={'space-between'} direction={'row'} alignItems={'center'}>
            {successContent ?? 'SUCCESS '}
            &nbsp;
            <CheckIcon color="success" sx={{ height: iconSize, width: iconSize }} />
          </Stack>,
        );
        if (onThen) {
          onThen();
        }
        break;
      case 'error':
        setButtonContent(
          <Stack
            width={'100%'}
            justifyContent={'space-between'}
            direction={'row'}
            alignItems={'center'}
            sx={{ color: statusRed[100] }}
          >
            {failedContent ?? 'FAILED '}
            <CloseIcon color="error" sx={{ height: iconSize, width: iconSize }} />
          </Stack>,
        );
        if (onCatch) {
          onCatch();
        }
        break;
    }
  }, [state, validation, confirmContent, failedContent, disabled]);

  return (
    <>
      {state === 'idle' ? (
        <Tooltip placement="top" title={validation?.helperText ?? ''}>
          <Box width={'100%'}>
            <ButtonWithIcon
              iconType="asterisk"
              disabled={buttonDisabled}
              onClick={props => {
                amplitude.track('transaction', { type: txKey, status: 'transaction-started' });
                if (onClick) {
                  onClick(props);
                }
              }}
              variant={variant}
              {...props}
            >
              {children}
            </ButtonWithIcon>
          </Box>
        </Tooltip>
      ) : (
        <Button
          sx={{ width: '100%', height: 42, ...props.sx }}
          variant={variant}
          disabled={buttonDisabled}
          onClick={() => {
            if (state === TxStatus.success || state === TxStatus.error) {
              createTxInstance({
                key: txKey,
                override: true,
              });
            }
          }}
        >
          {content}
        </Button>
      )}
    </>
  );
};
